import { c as _c } from "react/compiler-runtime";
import { Avatar, Badge, Button, Card, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import { AiAvatarBadge } from '@src/components/sco/Account/UserAvatar';
import { useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { BaseballCardData } from '@src/components/sco/Advisors/types';
import { useRouter } from 'next/router';
import { LuSparkles } from 'react-icons/lu';

/** Narrow cards (the Startup OS window) wrap a long label instead of spilling out of the button (#33). */
const wrappingButtonProps = {
  height: 'auto',
  lineHeight: 'short',
  minHeight: 8,
  paddingY: 1.5,
  whiteSpace: 'normal'
} as const;
export const BaseballCard = t0 => {
  const $ = _c(56);
  const {
    card
  } = t0;
  const router = useRouter();
  const {
    canAccessAdvisorTeam,
    canAccessPaidAdvisorTier
  } = useAdvisorsAccess();
  const href = card.primaryHref;
  const messageUnlocked = card.primaryPaywall === "team" ? canAccessAdvisorTeam : card.primaryPaywall === "paid" ? canAccessPaidAdvisorTier : true;
  const cardNavigates = !!href && messageUnlocked;
  const t1 = !!href;
  let t2;
  if ($[0] !== card.primaryLabel || $[1] !== href) {
    t2 = href ? <a href={href}>{card.primaryLabel}</a> : <>{card.primaryLabel}</>;
    $[0] = card.primaryLabel;
    $[1] = href;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <Button {...wrappingButtonProps} asChild={t1} size="sm" variant="cta" width="full" onClick={_temp}>{t2}</Button>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const primaryButton = t3;
  const t4 = cardNavigates ? "interactive" : "outline";
  let t5;
  if ($[6] !== cardNavigates || $[7] !== href || $[8] !== router) {
    t5 = cardNavigates ? () => router.push(href) : undefined;
    $[6] = cardNavigates;
    $[7] = href;
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== card.isAI) {
    t6 = card.isAI ? <Icon boxSize={5}><LuSparkles /></Icon> : null;
    $[10] = card.isAI;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== card.name || $[13] !== t6) {
    t7 = <Avatar.Fallback name={card.name}>{t6}</Avatar.Fallback>;
    $[12] = card.name;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = card.avatarUrl || undefined;
  let t9;
  if ($[15] !== t8) {
    t9 = <Avatar.Image src={t8} />;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== card.isAI) {
    t10 = card.isAI ? <AiAvatarBadge /> : null;
    $[17] = card.isAI;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7 || $[21] !== t9) {
    t11 = <Avatar.Root size="2xl">{t7}{t9}{t10}</Avatar.Root>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== card.name) {
    t12 = <Heading marginTop={2} size="md">{card.name}</Heading>;
    $[23] = card.name;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== card.role || $[26] !== card.rolePalette) {
    t13 = <Badge colorPalette={card.rolePalette} marginTop={2} textStyle="label2xs" variant="subtle">{card.role}</Badge>;
    $[25] = card.role;
    $[26] = card.rolePalette;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== card.bio) {
    t14 = <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="xs" lineClamp={4} marginTop={3} minHeight="52px">{card.bio}</Text>;
    $[28] = card.bio;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== card.primaryPaywall || $[31] !== primaryButton) {
    t15 = card.primaryPaywall ? <PaywalledAdvisorAction isTeamAdvisor={card.primaryPaywall === "team"} upgradeCtaButtonProps={{
      size: "sm",
      width: "full"
    }}>{primaryButton}</PaywalledAdvisorAction> : primaryButton;
    $[30] = card.primaryPaywall;
    $[31] = primaryButton;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== card.isAI || $[34] !== card.secondaryExternal || $[35] !== card.secondaryHref || $[36] !== card.secondaryLabel) {
    t16 = card.secondaryHref && card.secondaryLabel ? <Button {...wrappingButtonProps} asChild={true} size="sm" variant="outline" width="full" onClick={_temp2}><a href={card.secondaryHref} rel={card.secondaryExternal === false ? undefined : "noopener noreferrer"} target={card.secondaryExternal === false ? undefined : "_blank"}>{card.secondaryLabel}</a></Button> : card.isAI && card.secondaryLabel ? <Button {...wrappingButtonProps} disabled={true} size="sm" title="Coming soon" variant="outline" width="full" onClick={_temp3}>{card.secondaryLabel}</Button> : null;
    $[33] = card.isAI;
    $[34] = card.secondaryExternal;
    $[35] = card.secondaryHref;
    $[36] = card.secondaryLabel;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== card.tertiaryExternal || $[39] !== card.tertiaryHref || $[40] !== card.tertiaryLabel) {
    t17 = card.tertiaryHref && card.tertiaryLabel ? <Button {...wrappingButtonProps} asChild={true} size="sm" variant="outline" width="full" onClick={_temp4}><a href={card.tertiaryHref} rel={card.tertiaryExternal === false ? undefined : "noopener noreferrer"} target={card.tertiaryExternal === false ? undefined : "_blank"}>{card.tertiaryLabel}</a></Button> : null;
    $[38] = card.tertiaryExternal;
    $[39] = card.tertiaryHref;
    $[40] = card.tertiaryLabel;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t15 || $[43] !== t16 || $[44] !== t17) {
    t18 = <VStack align="stretch" gap={2} marginTop={4} width="full">{t15}{t16}{t17}</VStack>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t14 || $[50] !== t18) {
    t19 = <Card.Body alignItems="center" gap={0} textAlign="center">{t11}{t12}{t13}{t14}{t18}</Card.Body>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t19 || $[53] !== t4 || $[54] !== t5) {
    t20 = <Card.Root overflow="hidden" variant={t4} onClick={t5}>{t19}</Card.Root>;
    $[52] = t19;
    $[53] = t4;
    $[54] = t5;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
};
function _temp(event) {
  return event.stopPropagation();
}
function _temp2(event_0) {
  return event_0.stopPropagation();
}
function _temp3(event_1) {
  return event_1.stopPropagation();
}
function _temp4(event_2) {
  return event_2.stopPropagation();
}